import * as React from "react";

import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { User } from "./User";

export enum Status {
    Idle,
    Loading,
    Complete,
    Error
}

interface IIdleState {
    status: Status.Idle
}

interface ILoadingState {
    status: Status.Loading
}

interface IErrorState {
    status: Status.Error;

    readonly message: string;
}

interface ICompleteState<T> {
    status: Status.Complete;

    readonly data: T;
    readonly headers: any;
}

export type RequestState<T> = IIdleState | ILoadingState | IErrorState | ICompleteState<T>;

export interface IHttpRequest {
    readonly method: "get" | "post" | "delete";
    readonly url: string;
    readonly data?: any;
    readonly user?: User;
    readonly config?: AxiosRequestConfig;
}

async function setupRequestConfig(request: IHttpRequest): Promise<AxiosRequestConfig | undefined> {
    let config = request.config ?? { };

    if (request.user !== undefined) {
        const token = await request.user.getTokenAsync();

        if (config.headers === undefined) {
            config.headers = {};
        }

        config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
}

export function useHttp<T>(request: IHttpRequest | null): RequestState<T> {
    const [state, setState] = React.useState<RequestState<T>>({ status: Status.Idle });

    React.useEffect(() => {
        let disposed = false;

        const load = async () => {
            if (request === null) {
                return;
            }

            setState({ status: Status.Loading });

            try {
                const config = await setupRequestConfig(request);
                let promise: Promise<AxiosResponse<T>>;

                switch (request.method) {
                    case "get":
                        promise = axios.get<T>(request.url, config);
                        break;
                    case "post":
                        promise = axios.post<T>(request.url, request.data, config);
                        break;
                    case "delete":
                        promise = axios.delete<T>(request.url, config);
                        break;
                }

                const result = await promise;

                if (!disposed) {
                    setState({
                        status: Status.Complete,
                        data: result.data,
                        headers: result.headers
                    });
                }
            } catch (error) {
                if (!disposed) {
                    setState({
                        status: Status.Error,
                        message: "HTTP request failed."
                    });
                }
            }
        }

        load();

        return function cleanup() {
            disposed = true;
        }
    },
    [request]);

    return state;
}

export function useHttpGet<T>(url: string | null, user?: User): RequestState<T> {
    const [request, setRequest] = React.useState<IHttpRequest | null>(null);

    React.useEffect(() => {
        if (url !== null) {
            setRequest({
                method: "get",
                url,
                user: user !== null ? user : undefined
            })
        } else {
            setRequest(null);
        }
    },
    [url, user])
    
    return useHttp<T>(request);
}

/*
export function useHttpGet<T>(url: string | null, user: IUser | null = null): RequestState<T> {
    const [state, setState] = React.useState<RequestState<T>>({ status: Status.Idle });

    React.useEffect(() => {
        let disposed = false;

        const load = async () => {
            if (url === null) {
                return;
            }

            setState({ status: Status.Loading });

            try {
                const token = user !== null ? await user.getTokenAsync() : "";

                const config = setupRequestConfig(url, token);
                const result = await axios.get<T>(url, config);

                if (!disposed) {
                    setState({
                        status: Status.Complete,
                        data: result.data
                    });
                }
            } catch (error) {
                if (!disposed) {
                    setState({ status: Status.Error });
                }
            }
        }

        load();

        return function cleanup() {
            disposed = true;
        }
    },
    [url, user]);

    return state;
}

export function useHttpPost<TPost, TResponse>(url: string | null, data: TPost | null = null, user: IUser | null = null): RequestState<TResponse> {
    const [state, setState] = React.useState<RequestState<TResponse>>({ status: Status.Idle });

    React.useEffect(() => {
        let disposed = false;

        const load = async () => {
            if (url === null) {
                return;
            }

            setState({ status: Status.Loading });

            try {
                const token = user !== null ? await user.getTokenAsync() : "";
                const config = setupRequestConfig(url, token);
                const result = await axios.post<TResponse>(url, data, config);

                if (!disposed) {
                    setState({
                        status: Status.Complete,
                        data: result.data
                    });
                }
            } catch {
                if (!disposed) {
                    setState({ status: Status.Error });
                }
            }
        }

        load();

        return function cleanup() {
            disposed = true;
        }
    },
    [url, data, user]);

    return state;
}

export function useHttpDelete(url: string | null, user: IUser | null = null): RequestState<any> {
    const [state, setState] = React.useState<RequestState<any>>({ status: Status.Idle });

    React.useEffect(() => {
        let disposed = false;

        const load = async () => {
            if (url === null) {
                return;
            }

            setState({ status: Status.Loading });

            try {
                const token = user !== null ? await user.getTokenAsync() : "";
                const config = setupRequestConfig(url, token);
                const result = await axios.delete(url, config);

                if (!disposed) {
                    setState({
                        status: Status.Complete,
                        data: result.data
                    });
                }
            } catch {
                if (!disposed) {
                    setState({ status: Status.Error });
                }
            }
        }

        load();

        return function cleanup() {
            disposed = true;
        }
    },
    [url, user]);

    return state;
}*/